import React, { useCallback, useEffect } from 'react';
import { EventScheduledEvent } from 'react-calendly';
import styled from 'styled-components';

import {
  generateCalendlyScheduledBody,
  usePostCalendlyScheduledEvent,
} from '@front/pro/src/queries/calendly-scheduled-queries';
import { useSubscribedPro, useUniverse } from '@front/pro/src/signup/signup-form-context';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Alert from '@front/web-ui/src/components/alert/alert';
import Button from '@front/web-ui/src/components/button/button';

import { Screen } from '../../components/screen/screen';
import CalendlyWidget from '../../components/calendly-widget/calendly-widget';
import { getRoute } from '../../routes/routes';
import { useNavigateHistoryWithQuery } from '../../hooks/use-navigate-history-with-query';
import ButtonBar from '../../components/button-bar/button-bar';
import { logSignupStepEvent, proAccountCreated } from '../../utils/tracking';
import { useWebStepper } from '../../hooks/use-web-stepper';
import { useRedirectionStep } from '../../hooks/use-redirection-step';

const t = translate('screens.appointment');

const StyledAlert = styled(Alert)`
  margin-top: 20px;
`;

const Appointment = () => {
  useRedirectionStep();
  const { subscribedPro, setSubscribedPro } = useSubscribedPro();
  const { nextStep } = useWebStepper();
  const { historyPushWithQuery } = useNavigateHistoryWithQuery();
  const { scheduledCalendlyPost, isScheduledCalendlyError } = usePostCalendlyScheduledEvent();
  const { universe } = useUniverse();

  useEffect(() => {
    // In some browser, focus on the input is not enough to view the title of the page so we are scrolling to the top
    // eslint-disable-next-line no-multi-assign
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!subscribedPro) return;
    proAccountCreated(subscribedPro);
  }, [subscribedPro]);

  const handleOnEventScheduled = useCallback(
    (e: EventScheduledEvent) => {
      if (!subscribedPro?.email || !subscribedPro?.phone) return;
      const body = generateCalendlyScheduledBody({
        email: subscribedPro.email,
        phone: subscribedPro.phone,
        event: e.data.payload.event.uri,
      });
      scheduledCalendlyPost(body, {
        onSuccess: async () => {
          historyPushWithQuery(getRoute('confirmation'));
          logSignupStepEvent({ step: nextStep, universe });
          setSubscribedPro({
            ...subscribedPro,
            pro_account: {
              ...subscribedPro.pro_account,
              onboarding_appointment: {
                ...subscribedPro.pro_account.onboarding_appointment,
                interview_at: 'scheduled',
              },
            },
          });
        },
      });
    },
    [historyPushWithQuery, nextStep, scheduledCalendlyPost, setSubscribedPro, subscribedPro, universe],
  );

  const handleNext = useCallback(() => historyPushWithQuery(getRoute('confirmation')), [historyPushWithQuery]);

  if (subscribedPro?.pro_account.onboarding_appointment.interview_at) {
    return (
      <Screen title={t('.title')} isCenteredOnDesktop={false}>
        <StyledAlert type='success' content={t('.event_already_scheduled')} />
        <ButtonBar>
          <Button size='lg' onPress={handleNext}>
            {t('base.next')}
          </Button>
        </ButtonBar>
      </Screen>
    );
  }

  return (
    <Screen title={t('.title')} isCenteredOnDesktop={false}>
      <Text>{t('.description')}</Text>
      {isScheduledCalendlyError && <StyledAlert type='error' content='Something went wrong' />}
      <CalendlyWidget
        onEventScheduled={handleOnEventScheduled}
        prefilledInformations={{
          email: subscribedPro?.email,
          name: subscribedPro?.pro_account.account_name,
          phone: subscribedPro?.phone,
        }}
        showDetails={false}
        url={subscribedPro?.pro_account.onboarding_appointment.calendly_url || ''}
      />
    </Screen>
  );
};

export default Appointment;
